import { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { UserPlus, Mail, Edit, Trash2, Search, Shield, User } from 'lucide-react';
import { toast } from 'sonner';

const StaffManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [inviteForm, setInviteForm] = useState({
    email: '',
    name: '',
    role: 'driver',
    filiale: 'main'
  });

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      const res = await axios.get('/admin/users');
      setUsers(res.data);
    } catch (error) {
      console.error('Error loading users:', error);
      toast.error('Fehler beim Laden der Mitarbeiter');
    } finally {
      setLoading(false);
    }
  };

  const handleSendInvite = async () => {
    if (!inviteForm.email || !inviteForm.name) {
      toast.error('Bitte alle Felder ausfüllen');
      return;
    }

    try {
      const res = await axios.post('/auth/invite', inviteForm);
      toast.success('Einladung versendet');
      
      // Show invite link
      if (res.data.invite_link) {
        navigator.clipboard.writeText(res.data.invite_link);
        toast.success('Einladungslink in Zwischenablage kopiert');
      }
      
      setInviteDialogOpen(false);
      setInviteForm({ email: '', name: '', role: 'driver', filiale: 'main' });
    } catch (error) {
      console.error('Error sending invite:', error);
      toast.error('Fehler beim Versenden der Einladung');
    }
  };

  const handleUpdateRole = async (userId, newRole) => {
    try {
      await axios.put(`/admin/users/${userId}/role`, { role: newRole });
      toast.success('Rolle aktualisiert');
      loadUsers();
    } catch (error) {
      console.error('Error updating role:', error);
      toast.error('Fehler beim Aktualisieren');
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    if (!window.confirm(`Mitarbeiter ${userName} wirklich löschen?`)) return;

    try {
      await axios.delete(`/admin/users/${userId}`);
      toast.success('Mitarbeiter gelöscht');
      loadUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error('Fehler beim Löschen');
    }
  };

  const filteredUsers = users.filter(u => 
    u.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="staff-management">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-4xl md:text-5xl font-black tracking-tight uppercase text-white mb-2">
            Mitarbeiterverwaltung
          </h1>
          <p className="text-zinc-400">Team-Mitglieder und Berechtigungen verwalten</p>
        </div>
        <Dialog open={inviteDialogOpen} onOpenChange={setInviteDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-primary hover:bg-primary/90" data-testid="invite-staff-btn">
              <UserPlus className="mr-2 h-4 w-4" />
              Mitarbeiter einladen
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-zinc-900 border-zinc-800">
            <DialogHeader>
              <DialogTitle className="text-white">Neuen Mitarbeiter einladen</DialogTitle>
              <DialogDescription>
                Senden Sie eine Einladung per E-Mail
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="name">Name *</Label>
                <Input
                  id="name"
                  value={inviteForm.name}
                  onChange={(e) => setInviteForm({...inviteForm, name: e.target.value})}
                  placeholder="Max Mustermann"
                  className="bg-zinc-950 border-zinc-800"
                  data-testid="name-input"
                />
              </div>
              <div>
                <Label htmlFor="email">E-Mail *</Label>
                <Input
                  id="email"
                  type="email"
                  value={inviteForm.email}
                  onChange={(e) => setInviteForm({...inviteForm, email: e.target.value})}
                  placeholder="max@zozo-burger.de"
                  className="bg-zinc-950 border-zinc-800"
                  data-testid="email-input"
                />
              </div>
              <div>
                <Label htmlFor="role">Rolle *</Label>
                <Select value={inviteForm.role} onValueChange={(value) => setInviteForm({...inviteForm, role: value})}>
                  <SelectTrigger className="bg-zinc-950 border-zinc-800" data-testid="role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">
                    <SelectItem value="driver">Fahrer</SelectItem>
                    <SelectItem value="admin">Administrator</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="filiale">Filiale *</Label>
                <Select value={inviteForm.filiale} onValueChange={(value) => setInviteForm({...inviteForm, filiale: value})}>
                  <SelectTrigger className="bg-zinc-950 border-zinc-800">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">
                    <SelectItem value="main">Hauptfiliale</SelectItem>
                    <SelectItem value="branch1">Filiale 1</SelectItem>
                    <SelectItem value="branch2">Filiale 2</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <Button onClick={handleSendInvite} className="flex-1" data-testid="send-invite-btn">
                <Mail className="mr-2 h-4 w-4" />
                Einladung senden
              </Button>
              <Button variant="outline" onClick={() => setInviteDialogOpen(false)} className="border-zinc-700">
                Abbrechen
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-3 h-4 w-4 text-zinc-500" />
        <Input
          placeholder="Suche nach Name oder E-Mail..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10 bg-zinc-900 border-zinc-800"
          data-testid="search-input"
        />
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="pt-6">
            <div className="text-center">
              <div className="text-3xl font-bold text-white">{users.length}</div>
              <div className="text-sm text-zinc-400 mt-1">Gesamt Mitarbeiter</div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="pt-6">
            <div className="text-center">
              <div className="text-3xl font-bold text-blue-400">{users.filter(u => u.role === 'driver').length}</div>
              <div className="text-sm text-zinc-400 mt-1">Fahrer</div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="pt-6">
            <div className="text-center">
              <div className="text-3xl font-bold text-amber-400">{users.filter(u => u.role === 'admin').length}</div>
              <div className="text-sm text-zinc-400 mt-1">Administratoren</div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Table */}
      <Card className="bg-zinc-900 border-zinc-800">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-zinc-800 hover:bg-zinc-900">
                  <TableHead className="text-zinc-400">Name</TableHead>
                  <TableHead className="text-zinc-400">E-Mail</TableHead>
                  <TableHead className="text-zinc-400">Rolle</TableHead>
                  <TableHead className="text-zinc-400">Filiale</TableHead>
                  <TableHead className="text-zinc-400">Registriert</TableHead>
                  <TableHead className="text-zinc-400 text-right">Aktionen</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredUsers.map((user) => (
                  <TableRow key={user.user_id} className="border-zinc-800 hover:bg-zinc-800" data-testid={`user-row-${user.user_id}`}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-full bg-zinc-700 flex items-center justify-center">
                          {user.role === 'admin' ? (
                            <Shield className="h-5 w-5 text-amber-400" />
                          ) : (
                            <User className="h-5 w-5 text-zinc-400" />
                          )}
                        </div>
                        <span className="font-medium text-white">{user.name}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-zinc-300">{user.email}</TableCell>
                    <TableCell>
                      <Select 
                        value={user.role} 
                        onValueChange={(value) => handleUpdateRole(user.user_id, value)}
                      >
                        <SelectTrigger className="w-32 bg-zinc-800 border-zinc-700 h-8">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-zinc-900 border-zinc-800">
                          <SelectItem value="driver">Fahrer</SelectItem>
                          <SelectItem value="admin">Admin</SelectItem>
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary" className="capitalize">
                        {user.filiale || 'Hauptfiliale'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-zinc-400 text-sm">
                      {new Date(user.created_at).toLocaleDateString('de-DE')}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-red-400 hover:text-red-300 hover:bg-red-950"
                        onClick={() => handleDeleteUser(user.user_id, user.name)}
                        data-testid={`delete-user-${user.user_id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          {filteredUsers.length === 0 && (
            <div className="text-center py-12 text-zinc-500">
              {searchTerm ? 'Keine Mitarbeiter gefunden' : 'Noch keine Mitarbeiter'}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default StaffManagement;
